import { useCallback, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import CascadeRangePicker from "@/components/CascadeRangePicker";

/**
 * 17th tab — 120-year Vimshottari Dasha CSV export + Daily Dasha Cascade PDF ZIP job.
 */
export default function VimshottariCsvPanel({ profile }) {
  // Life-event search state
  const today = new Date().toISOString().slice(0, 10);
  const [searchDate, setSearchDate] = useState(today);
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);

  const searchDay = async () => {
    setSearching(true);
    setSearchResult(null);
    try {
      const r = await api.dashaAt(profile.profile_id, searchDate);
      setSearchResult(r);
    } catch (err) {
      console.error("dasha search failed:", err);
      toast.error(err?.response?.data?.detail || "Date is outside this native's 120-year cycle");
    }
    setSearching(false);
  };

  const download = () => {
    window.open(api.vimshottariCsvUrl(profile.profile_id), "_blank");
  };

  if (!profile) return null;

  return (
    <div className="space-y-6" data-testid="vim-csv-panel">
      <div className="flex items-start gap-2 border-l-4 border-[#D4AF37] pl-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">
            120-Year Vimshottari Dasha · CSV & Daily Cascade
          </div>
          <div className="text-[10px] text-[#6E6B64] font-mono mt-1">
            Complete 5-level cascade (Maha → Antar → Pratyantar → Sookshma → Prana) · CSV data + day-by-day PDF ZIP
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border border-[#2A2D35] bg-[#181A1F] p-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#6E6B64] font-mono mb-1">Native</div>
          <div className="font-heading text-2xl text-[#E6E1D6]">{profile.name}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#6E6B64] font-mono mb-1">Birth</div>
          <div className="font-mono text-sm text-[#E6E1D6]">{profile.dob} · {profile.tob}</div>
          <div className="font-mono text-[11px] text-[#A19E95] mt-1">{profile.place_name}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#6E6B64] font-mono mb-1">Coordinates</div>
          <div className="font-mono text-xs text-[#E6E1D6]">{profile.lat}, {profile.lon}</div>
          <div className="font-mono text-[11px] text-[#A19E95] mt-1">{profile.tz}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#6E6B64] font-mono mb-1">Ayanamsha</div>
          <div className="font-heading text-xl text-[#A8896C] capitalize">{profile.ayanamsha}</div>
        </div>
      </div>

      <div className="border border-[#2A2D35] bg-[#181A1F] p-5 space-y-3">
        <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">
          Life-Event Search · Which Prana on this date?
        </div>
        <div className="text-sm text-[#E6E1D6] leading-relaxed">
          Pick any date within this native's 120-year cycle to see the exact
          Maha / Antar / Pratyantar / Sookshma lords and every Pranadasha ruling that day —
          great for retrofitting real life events against the dasha timeline.
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono mb-2">
              Event Date
            </div>
            <Input data-testid="lifesearch-date" type="date" value={searchDate}
                   onChange={(e) => setSearchDate(e.target.value)}
                   className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono w-44" />
          </div>
          <Button data-testid="lifesearch-btn" onClick={searchDay} disabled={searching}
                  className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158]">
            {searching
              ? <><Loader2 size={14} className="mr-2 animate-spin" /> Searching…</>
              : <><Search size={14} className="mr-2" /> Find Dasha on this Day</>}
          </Button>
        </div>

        {searchResult && (
          <div className="space-y-4 pt-3" data-testid="lifesearch-result">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 border-t border-[#2A2D35] pt-3">
              {[
                ["Mahadasha", searchResult.mahadasha, "#D4AF37"],
                ["Antardasha", searchResult.antardasha, "#A8896C"],
                ["Pratyantardasha", searchResult.pratyantardasha, "#B58A5C"],
                ["Sookshmadasha", searchResult.sookshmadasha, "#7A9DB4"],
              ].map(([label, lord, color]) => (
                <div key={label} className="bg-[#0F1115] px-3 py-2 rounded-sm border border-[#2A2D35]">
                  <div className="text-[9px] uppercase tracking-widest font-mono text-[#6E6B64]">{label}</div>
                  <div className="font-heading text-xl" style={{ color }}>{lord}</div>
                </div>
              ))}
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-widest font-mono text-[#A8896C] mb-2">
                {searchResult.date} · {searchResult.weekday} — {searchResult.pranadashas.length} Pranadasha{searchResult.pranadashas.length !== 1 ? "s" : ""} ruling
              </div>
              <div className="border border-[#2A2D35] bg-[#0F1115] overflow-x-auto">
                <table className="w-full font-mono text-[11px]">
                  <thead className="text-[#6E6B64]">
                    <tr>
                      {["#", "Prana Lord", "From", "To", "Hrs", "Prediction"].map(h => (
                        <th key={h} className="px-3 py-2 text-left border-b border-[#2A2D35]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {searchResult.pranadashas.map((pr, i) => (
                      <tr key={`${pr.prana}-${i}`} className="border-b border-[#181A1F]"
                          data-testid={`lifesearch-prana-${i}`}>
                        <td className="px-3 py-2 text-[#6E6B64]">{i + 1}</td>
                        <td className="px-3 py-2 text-[#D4AF37] font-heading">{pr.prana}</td>
                        <td className="px-3 py-2 text-[#E6E1D6]">{pr.from}</td>
                        <td className="px-3 py-2 text-[#E6E1D6]">{pr.to}</td>
                        <td className="px-3 py-2 text-[#A19E95]">{pr.hours}</td>
                        <td className="px-3 py-2 text-[#A19E95] leading-snug max-w-[500px]">{pr.prediction}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-widest font-mono text-[#A8896C] mb-2">
                Planets in this day's sub-dashas
              </div>
              <div className="border border-[#2A2D35] bg-[#0F1115] overflow-x-auto">
                <table className="w-full font-mono text-[11px]">
                  <thead className="text-[#6E6B64]">
                    <tr>
                      {["Planet", "Sign", "Deg", "Avastha", "Factor", "Strength %", "Str", "R"].map(h => (
                        <th key={h} className="px-3 py-2 text-left border-b border-[#2A2D35]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {searchResult.planets.map((p) => (
                      <tr key={p.planet} className="border-b border-[#181A1F]">
                        <td className="px-3 py-2 text-[#D4AF37] font-heading">{p.planet}</td>
                        <td className="px-3 py-2 text-[#E6E1D6]">{p.sign_short}</td>
                        <td className="px-3 py-2 text-[#A19E95]">{p.deg}°</td>
                        <td className="px-3 py-2 text-[#E6E1D6]">{p.avastha}</td>
                        <td className="px-3 py-2 text-[#6E6B64]">×{p.avastha_factor.toFixed(2)}</td>
                        <td className="px-3 py-2 text-[#E6E1D6]">{p.pct.toFixed(1)}%</td>
                        <td className="px-3 py-2 text-[#A19E95]">{p.strength.toFixed(1)}</td>
                        <td className="px-3 py-2 text-[#C15C3D]">{p.retrograde ? "R" : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="border border-[#2A2D35] bg-[#181A1F] p-5 space-y-3">
        <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">
          Option 1 · CSV (Spreadsheet Data)
        </div>
        <div className="text-sm text-[#E6E1D6] leading-relaxed">
          Downloads a single CSV row per Prana period. Best for filtering in Excel / Google Sheets
          — search a specific life event date to see which Prana was active. <br/>
          <span className="text-[10px] text-[#6E6B64] font-mono">~63,000 rows · 15 MB · instant download</span>
        </div>
        <Button data-testid="vim-csv-download-btn" onClick={download}
                className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158]">
          <Download size={16} className="mr-2" /> Download 120-Year Dasha CSV
        </Button>
      </div>

      <div className="border border-[#2A2D35] bg-[#181A1F] p-5 space-y-3">
        <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">
          Option 2 · Daily Cascade PDF · Custom Range
        </div>
        <div className="text-sm text-[#E6E1D6] leading-relaxed">
          Pick any range and download a PDF instantly — no waiting jobs, no ZIP.
          Each day is a horizontal data-sheet block showing active Mahadasha/Antar/Prat/Sookshma,
          all Pranadashas with time windows + predictions, and only the planets involved in that
          day's 5 sub-dashas with their Avastha and Shadbala strength %.
        </div>
        <ul className="text-[11px] text-[#A19E95] font-mono space-y-0.5 pl-4">
          <li>• <b className="text-[#E6E1D6]">Single Day</b> — 1-page snapshot for a specific date</li>
          <li>• <b className="text-[#E6E1D6]">Week</b> — 7 days from a chosen start date</li>
          <li>• <b className="text-[#E6E1D6]">Month</b> — pick year + month</li>
          <li>• <b className="text-[#E6E1D6]">Full Year</b> — pick any year in the 120-yr cycle</li>
          <li>• <b className="text-[#E6E1D6]">Mahadasha</b> — the entire span of one Mahadasha (Jupiter, Saturn, Mercury, …)</li>
        </ul>
        <CascadeRangePicker profile={profile} />
      </div>

      <div className="text-[10px] text-[#6E6B64] font-mono italic pt-2">
        Both exports use this profile's stored ayanamsha (<b>{profile.ayanamsha}</b>) and are generated
        fresh from Swiss Ephemeris. The daily cascade uses BPHS Shadbala sampled at 12:30 local for each day.
      </div>
    </div>
  );
}
